import React, { FC, useMemo, ChangeEvent, useCallback } from "react";
import CheckboxGroup from "../../Unknown/CheckboxGroup";
import Box from "../../Unknown/Box";
import { AddOn, CenterConfig, CenterServicesState } from "./types";
import parseAddOn from "./parseAddOn";

export type ServiceOption = {
  groupId: string;
  localId: string;
  value: string;
  label: string;
  price: string;
  description: string;
  discountPrice?: string;
  isDisabled?: boolean;
  addOnIds?: string[];
  metadata?: Record<string, string | boolean | number | null>;
};

export interface SelectStudyAddOnsFormProps {
  currentServiceId: string;
  addOns: AddOn[] | null;
  centerConfig: CenterConfig;
  isSubmitting: boolean;
  isStatLocked?: boolean;
  selectedCenterServices: CenterServicesState;
  setSelectedCenterServices: React.Dispatch<
    React.SetStateAction<CenterServicesState>
  >;
}

const SelectStudyAddOnsForm: FC<SelectStudyAddOnsFormProps> = ({
  addOns,
  centerConfig,
  isSubmitting,
  isStatLocked = false,
  selectedCenterServices,
  setSelectedCenterServices,
  currentServiceId,
}) => {
  const onQuantityChange = useCallback(
    (addOnId: string, quantity: number): void => {
      setSelectedCenterServices((prev) =>
        prev.map((service) => {
          if (service?.localId !== currentServiceId) return service;

          return {
            ...service,
            addOnQuantities: {
              ...service.addOnQuantities,
              [addOnId]: quantity,
            },
          };
        }),
      );
    },
    [setSelectedCenterServices, currentServiceId],
  );

  const options = useMemo(() => {
    if (!centerConfig || !addOns?.length) return [];

    const { defaultCurrencyCode } = centerConfig;

    const centerService = selectedCenterServices.find(
      (service) => service?.localId === currentServiceId,
    );

    return addOns
      .filter((addOn) => addOn.appliesTo === "study")
      .map((addOn) => {
        const checked = !!centerService?.addOnIds?.some(
          (addOnId) => addOnId === addOn.id,
        );
        const quantity = centerService?.addOnQuantities?.[addOn.id] ?? 1;

        return parseAddOn({
          addOn,
          currencyCode: defaultCurrencyCode,
          checked,
          offeringsCount: centerService?.studyIds?.length || 0,
          quantity,
          disabled: isStatLocked && addOn.type === "stat",
          onQuantityChange: addOn.allowMultiplePurchases
            ? (newQuantity: number) => onQuantityChange(addOn.id, newQuantity)
            : undefined,
        });
      });
  }, [
    addOns,
    centerConfig,
    currentServiceId,
    selectedCenterServices,
    isStatLocked,
    onQuantityChange,
  ]);

  const onChecked = useCallback(
    (addOnId?: string): void => {
      if (!centerConfig || !addOns?.length || !addOnId) return;
      setSelectedCenterServices((prev) => {
        const selectedService = prev.find(
          (service) => service?.localId === currentServiceId,
        );

        if (selectedService) {
          const updatedAddOnIds = selectedService.addOnIds
            ? [...selectedService.addOnIds, addOnId]
            : [addOnId];

          return prev.map((service) =>
            service?.localId === currentServiceId
              ? {
                  ...service,
                  addOnIds: updatedAddOnIds,
                  addOnQuantities: {
                    ...service.addOnQuantities,
                    [addOnId]: 1,
                  },
                }
              : service,
          );
        }

        return prev;
      });
    },
    [addOns, centerConfig, setSelectedCenterServices, currentServiceId],
  );

  const onUnchecked = useCallback(
    (addOnId?: string) => {
      if (!addOnId) return;

      setSelectedCenterServices((prev) => {
        const updatedServices = prev.map((service) => {
          if (service?.localId === currentServiceId) {
            return {
              ...service,
              addOnIds: service.addOnIds?.filter((id) => id !== addOnId) || [],
            };
          }
          return service;
        });

        return updatedServices;
      });
    },
    [setSelectedCenterServices, currentServiceId],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const actionFunc = checked ? onChecked : onUnchecked;
      actionFunc(e?.target?.value);
    },
    [onChecked, onUnchecked],
  );

  if (!centerConfig || !addOns?.length) return null;

  return (
    <Box mt={2}>
      <CheckboxGroup
        dense
        options={options}
        onChange={onChange}
        disabled={isSubmitting}
      />
    </Box>
  );
};

export default SelectStudyAddOnsForm;
